import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { get } from '../lib/api';
import { setAppBadge } from '../lib/push';
import { Avatar, Bar, ErrorNote, Empty, Spinner } from '../components/ui';
import { money, percent, periodLabel } from '../lib/format';
import { tone } from './KidHome';
import type { OverviewChild, Settings } from '../lib/types';

interface Overview {
  date: string;
  week: string;
  month: string;
  children: OverviewChild[];
  settings: Pick<Settings, 'householdName' | 'currency' | 'timezone'>;
}

export default function ParentHome(): JSX.Element {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    get<Overview>('/api/overview')
      .then((res) => {
        setData(res);
        // Parents' icon count is what is waiting on them.
        setAppBadge(res.children.reduce((sum, c) => sum + c.pendingClaims, 0));
      })
      .catch((err) => setError((err as Error).message));
  }, []);

  if (error) return <ErrorNote error={error} />;
  if (!data) return <Spinner />;

  return (
    <>
      <h1>{data.settings.householdName}</h1>
      <p className="muted small">
        {periodLabel('week', data.week)} · {periodLabel('month', data.month)}
      </p>

      {data.children.length === 0 && (
        <Empty icon="🧒">
          <p>No kids yet.</p>
          <Link className="btn" to="/family">
            Add a child
          </Link>
        </Empty>
      )}

      <div className="stack" style={{ marginTop: 14 }}>
        {data.children.map((entry) => {
          const useMonthly = entry.month.allowance > 0 && entry.week.allowance === 0;
          const pay = useMonthly ? entry.month : entry.week;
          return (
            <Link
              key={entry.child.id}
              to={`/child/${entry.child.id}`}
              className="card"
              style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
            >
              <div className="row">
                <Avatar emoji={entry.child.emoji} />
                <div className="grow">
                  <div className="row between">
                    <strong>{entry.child.name}</strong>
                    <span className={`badge ${pay.qualifies ? 'good' : 'bad'}`}>
                      {pay.qualifies ? 'Qualifies' : 'Not yet'}
                    </span>
                  </div>
                  <div className="tiny muted">
                    Today: {entry.today.done} done
                    {entry.today.due > 0 && ` · ${entry.today.due} left`}
                    {entry.today.overdue > 0 && ` · ${entry.today.overdue} overdue`}
                    {entry.quietToday && ` · 🏅 ${entry.quietToday}`}
                    {entry.snoozedUntil && ' · 🔕 Snoozed today'}
                    {entry.streak > 0 && ` · 🔥 ${entry.streak}`}
                    {entry.pendingClaims > 0 && (
                      <>
                        {' · '}
                        <span className="badge warn">⭐ {entry.pendingClaims} to approve</span>
                      </>
                    )}
                    {entry.turnsUp.map((job) => (
                      <span key={job.id}>
                        {' · '}
                        <span className="badge turn">
                          {job.icon} {job.title} — their turn
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <Bar value={pay.rate} tone={tone(pay)} />
              </div>
              <div className="row between tiny muted" style={{ marginTop: 6 }}>
                <span>
                  {useMonthly ? 'This month' : 'This week'}: {pay.doneCount}/{pay.totalCount} ·{' '}
                  {percent(pay.rate)} (needs {percent(pay.threshold)})
                </span>
                <span>{money(pay.payout ? pay.payout.amount : pay.totalAmount, pay.currency)}</span>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="row" style={{ marginTop: 18, gap: 10 }}>
        <Link className="btn secondary grow center" to="/review">
          Review pocket money
        </Link>
        <Link className="btn secondary grow center" to="/family">
          Manage family
        </Link>
      </div>
    </>
  );
}
